import type {
  ContentPageReference,
  ContentViewReference,
  RelationshipPath,
} from "@/stores/tableInspect"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { Layer } from "@kaleidoscope-solutions/engine-wasm"
import yaml from "js-yaml"

/**
 * Builds a lightweight "content graph" from exported YAML layers so table/column
 * inspectors can answer questions that are not directly available from table WASM
 * APIs alone.
 *
 * High-level flow:
 * 1) Export all model layers to YAML chunks via engine APIs.
 * 2) Parse datasets/views/layout sections/widgets from YAML.
 * 3) Index relationships by type, and index where views are used in pages.
 * 4) Derive related relationship paths, views, and pages for panel metadata.
 */
type ModelLayerKey = "base" | "org" | "user"

interface ContentRelationshipEdge {
  datasetName: string
  fromType: string
  toType: string
  relationship?: string
  inverse?: string
}

interface ContentViewIndex {
  name: string
  tableType?: string
  datasetNames: string[]
  rowTypeNames: string[]
  columnTypeNames: string[]
}

interface ContentPageInfo {
  name: string
  parentName?: string
  contextTypes: string[]
}

interface ContentWidgetReference {
  pageName: string
  sectionTitle?: string
  sectionId?: string
  widgetTitle?: string
  widgetId?: string
  viewName: string
  contextTypes: string[]
}

interface ContentModelIndex {
  edgesByType: Map<string, ContentRelationshipEdge[]>
  views: ContentViewIndex[]
  pageInfoByName: Map<string, ContentPageInfo>
  widgetsByViewName: Map<string, ContentWidgetReference[]>
}

interface RelationshipDatasetLike {
  keyTypes?: string[]
  valueTypes?: string[]
}

let contentModelIndexPromises = new WeakMap<ModelFacade, Promise<ContentModelIndex>>()

function normaliseIdentifier(value: string): string {
  const trimmed = value.trim()
  if (trimmed.length < 2) {
    return trimmed.toLowerCase()
  }
  const first = trimmed[0]
  const last = trimmed[trimmed.length - 1]
  if ((first === "'" && last === "'") || (first === "\"" && last === "\"")) {
    return trimmed.slice(1, -1).trim().toLowerCase()
  }
  return trimmed.toLowerCase()
}

function uniqueStrings(values: string[]): string[] {
  const output: string[] = []
  const seen = new Set<string>()

  for (const value of values) {
    const trimmed = String(value).trim()
    if (!trimmed) {
      continue
    }
    const key = normaliseIdentifier(trimmed)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    output.push(trimmed)
  }

  return output
}

function sameIdentifier(left: string, right: string): boolean {
  return normaliseIdentifier(left) === normaliseIdentifier(right)
}

function safeString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed || undefined
}

function stringArray(values: unknown): string[] {
  if (!Array.isArray(values)) {
    return []
  }
  return uniqueStrings(values.map((value) => String(value)))
}

function parseYamlDocument(content: string): any | undefined {
  try {
    return yaml.load(content)
  } catch (error) {
    console.warn("[ContentModelIndex] Failed to parse yaml chunk", error)
    return undefined
  }
}

async function exportLayer(model: ModelFacade, layer: Layer): Promise<string[]> {
  const saveResult: any = await model.api.saveModelLayerToChunks(layer)
  return Array.isArray(saveResult?.chunks) ? saveResult.chunks.map(String) : []
}

async function allLayerContents(model: ModelFacade): Promise<string[]> {
  // We read all layers because content references can be split across base/org/user.
  const layerDescriptors: Array<{ layer: Layer; key: ModelLayerKey }> = [
    { layer: Layer.Base, key: "base" },
    { layer: Layer.Org, key: "org" },
    { layer: Layer.User, key: "user" },
  ]

  const exports = await Promise.all(layerDescriptors.map(async ({ layer, key }) => {
    try {
      return { key, chunks: await exportLayer(model, layer) }
    } catch (error) {
      console.warn(`[ContentModelIndex] Failed to export ${key} layer`, error)
      return {
        key,
        chunks: [],
      }
    }
  }))

  return exports.flatMap(({ chunks }) => chunks)
}

function extractQuotedOrBareItems(value: string): string[] {
  const items: string[] = []

  for (const match of value.matchAll(/'([^']+)'|([\w.:-]+)/g)) {
    const nextValue = safeString(match[1] ?? match[2])
    if (nextValue) {
      items.push(nextValue)
    }
  }

  return uniqueStrings(items)
}

function extractBracketItems(expression: unknown, label: "Dataset" | "Type"): string[] {
  if (typeof expression !== "string") {
    return []
  }

  const pattern = new RegExp(`\\[${label}:([^\\]]*)\\]`, "gi")
  const items: string[] = []

  for (const match of expression.matchAll(pattern)) {
    items.push(...extractQuotedOrBareItems(match[1] ?? ""))
  }

  return uniqueStrings(items)
}

function mergeIntoMapArray<T>(map: Map<string, T[]>, key: string, value: T) {
  const normalisedKey = normaliseIdentifier(key)
  if (!normalisedKey) {
    return
  }
  const existing = map.get(normalisedKey)
  if (existing) {
    existing.push(value)
    return
  }
  map.set(normalisedKey, [value])
}

function upsertPageInfo(pageInfoByName: Map<string, ContentPageInfo>, pageName: string): ContentPageInfo {
  const key = normaliseIdentifier(pageName)
  const existing = pageInfoByName.get(key)
  if (existing) {
    return existing
  }

  const created: ContentPageInfo = {
    name: pageName,
    contextTypes: [],
  }
  pageInfoByName.set(key, created)
  return created
}

function extractEntityTypesFromContext(context: unknown): string[] {
  if (!Array.isArray(context)) {
    return []
  }

  const types: string[] = []

  for (const item of context) {
    if (!item || typeof item !== "object") {
      continue
    }

    if (String((item as any).kind ?? "") !== "Entity") {
      continue
    }

    const content = (item as any).content
    if (typeof content === "string") {
      types.push(content)
      continue
    }

    if (!Array.isArray(content)) {
      continue
    }

    for (const contentItem of content) {
      if (typeof contentItem === "string") {
        types.push(contentItem)
        break
      }
    }
  }

  return uniqueStrings(types)
}

function extractEntityTypesFromSelectors(selectors: unknown): string[] {
  if (!Array.isArray(selectors)) {
    return []
  }

  return uniqueStrings(
    selectors
      .map((selector) => safeString((selector as any)?.entity_type))
      .filter((value): value is string => Boolean(value))
  )
}

function sectionTitlePath(titles: string[]): string | undefined {
  const filtered = titles.map((title) => title.trim()).filter(Boolean)
  if (filtered.length === 0) {
    return undefined
  }
  return filtered.join(" / ")
}

function collectLayoutSectionWidgets(
  section: any,
  pageName: string,
  inheritedContextTypes: string[],
  titlePath: string[],
  widgets: ContentWidgetReference[]
) {
  const nextTitle = safeString(section?.title)
  const nextTitlePath = nextTitle ? [...titlePath, nextTitle] : titlePath
  const contextTypes = uniqueStrings([
    ...inheritedContextTypes,
    ...extractEntityTypesFromContext(section?.context),
    ...extractEntityTypesFromSelectors(section?.entity_selectors),
  ])

  if (Array.isArray(section?.widgets)) {
    for (const widget of section.widgets) {
      const viewName = safeString(widget?.view)
      if (!viewName) {
        continue
      }

      widgets.push({
        pageName,
        sectionTitle: sectionTitlePath(nextTitlePath),
        sectionId: safeString(section?.id),
        widgetTitle: safeString(widget?.title),
        widgetId: safeString(widget?.id),
        viewName,
        contextTypes,
      })
    }
  }

  if (Array.isArray(section?.sections)) {
    for (const childSection of section.sections) {
      collectLayoutSectionWidgets(childSection, pageName, contextTypes, nextTitlePath, widgets)
    }
  }
}

function parseDatasets(doc: any, edgesByType: Map<string, ContentRelationshipEdge[]>) {
  // Build directional type edges from dataset key/value types so we can later
  // walk "relationship chains" in inspectors.
  const datasets = Array.isArray(doc?.content?.datasets) ? doc.content.datasets : []

  for (const dataset of datasets) {
    const datasetName = safeString(dataset?.name)
    const keyTypes = stringArray(dataset?.keytypes ?? dataset?.keyTypes)
    const valueTypes = stringArray(dataset?.valuetypes ?? dataset?.valueTypes)
    const relationship = safeString(dataset?.relationship)
    const inverse = safeString(dataset?.inverse)

    if (!datasetName || keyTypes.length === 0 || valueTypes.length === 0) {
      continue
    }

    for (const keyType of keyTypes) {
      for (const valueType of valueTypes) {
        mergeIntoMapArray(edgesByType, keyType, {
          datasetName,
          fromType: keyType,
          toType: valueType,
          relationship,
          inverse,
        })

        mergeIntoMapArray(edgesByType, valueType, {
          datasetName: inverse ?? datasetName,
          fromType: valueType,
          toType: keyType,
          relationship: inverse ?? relationship,
          inverse: datasetName,
        })
      }
    }
  }
}

function parseViews(doc: any, viewsByName: Map<string, ContentViewIndex>) {
  // Parse view-level type/dataset usage from YAML expressions (rows/columns)
  // to support "related views" matches.
  const views = Array.isArray(doc?.content?.views) ? doc.content.views : []

  for (const view of views) {
    const name = safeString(view?.name)
    if (!name) {
      continue
    }

    const rowTypeNames = extractBracketItems(view?.rows?.nesting, "Type")
      .filter((typeName) => !sameIdentifier(typeName, "Dataset"))
    const columnTypeNames = extractBracketItems(view?.columns?.nesting, "Type")
      .filter((typeName) => !sameIdentifier(typeName, "Dataset"))
    const datasetNames = uniqueStrings([
      ...extractBracketItems(view?.rows?.list, "Dataset"),
      ...extractBracketItems(view?.columns?.list, "Dataset"),
    ])

    viewsByName.set(normaliseIdentifier(name), {
      name,
      tableType: safeString(view?.table_type),
      datasetNames,
      rowTypeNames,
      columnTypeNames,
    })
  }
}

function parseLayouts(
  doc: any,
  pageInfoByName: Map<string, ContentPageInfo>,
  widgetReferences: ContentWidgetReference[]
) {
  // Capture where views are rendered in page widgets. This powers deep links
  // from relationship/dataset/type references back to pages.
  const projects = Array.isArray(doc?.content?.projects) ? doc.content.projects : []

  for (const project of projects) {
    const pages = Array.isArray(project?.pages) ? project.pages : []

    for (const page of pages) {
      const pageName = safeString(page?.name)
      if (!pageName) {
        continue
      }

      const pageInfo = upsertPageInfo(pageInfoByName, pageName)
      pageInfo.contextTypes = uniqueStrings([
        ...pageInfo.contextTypes,
        ...extractEntityTypesFromContext(page?.context),
        ...extractEntityTypesFromSelectors(page?.entity_selectors),
      ])

      if (Array.isArray(page?.widgets) || Array.isArray(page?.sections)) {
        collectLayoutSectionWidgets(page, pageName, pageInfo.contextTypes, [], widgetReferences)
      }
    }
  }
}

function parseUserPageContext(doc: any, pageInfoByName: Map<string, ContentPageInfo>) {
  const pages = doc?.content?.context?.pages
  if (!pages || typeof pages !== "object") {
    return
  }

  for (const [pageName, page] of Object.entries(pages)) {
    const nextPageName = safeString(pageName)
    if (!nextPageName) {
      continue
    }

    const pageInfo = upsertPageInfo(pageInfoByName, nextPageName)
    pageInfo.parentName = safeString((page as any)?.parent) ?? pageInfo.parentName
    pageInfo.contextTypes = uniqueStrings([
      ...pageInfo.contextTypes,
      ...extractEntityTypesFromContext((page as any)?.context),
    ])
  }
}

async function buildContentModelIndex(model: ModelFacade): Promise<ContentModelIndex> {
  // Single pass over all exported YAML docs to build reusable indices.
  const contents = await allLayerContents(model)
  const edgesByType = new Map<string, ContentRelationshipEdge[]>()
  const viewsByName = new Map<string, ContentViewIndex>()
  const pageInfoByName = new Map<string, ContentPageInfo>()
  const widgetReferences: ContentWidgetReference[] = []

  for (const content of contents) {
    const doc = parseYamlDocument(content)
    if (!doc || typeof doc !== "object") {
      continue
    }

    parseDatasets(doc, edgesByType)
    parseViews(doc, viewsByName)
    parseLayouts(doc, pageInfoByName, widgetReferences)
    parseUserPageContext(doc, pageInfoByName)
  }

  const widgetsByViewName = new Map<string, ContentWidgetReference[]>()

  for (const widgetReference of widgetReferences) {
    mergeIntoMapArray(widgetsByViewName, widgetReference.viewName, widgetReference)
  }

  return {
    edgesByType,
    views: [...viewsByName.values()].sort((left, right) => left.name.localeCompare(right.name)),
    pageInfoByName,
    widgetsByViewName,
  }
}

export function invalidateContentModelIndex(model?: ModelFacade) {
  if (model) {
    contentModelIndexPromises.delete(model)
    return
  }
  contentModelIndexPromises = new WeakMap<ModelFacade, Promise<ContentModelIndex>>()
}

export function getContentModelIndex(model: ModelFacade): Promise<ContentModelIndex> {
  // Cache by ModelFacade instance so repeated panel openings do not re-parse YAML.
  const existingPromise = contentModelIndexPromises.get(model)
  if (existingPromise) {
    return existingPromise
  }

  const nextPromise = buildContentModelIndex(model)
  contentModelIndexPromises.set(model, nextPromise)
  return nextPromise
}

export function findContentRelationshipPaths(
  index: ContentModelIndex,
  startTypes: string[],
  options?: {
    maxDepth?: number
    maxPaths?: number
  }
): RelationshipPath[] {
  // Breadth-first traversal over indexed type edges, bounded for UI readability.
  const maxDepth = options?.maxDepth ?? 2
  const maxPaths = options?.maxPaths ?? 8
  const queue = uniqueStrings(startTypes).map((typeName) => ({
    typeNames: [typeName],
    datasetNames: [] as string[],
  }))
  const paths: RelationshipPath[] = []
  const seen = new Set<string>()

  while (queue.length > 0 && paths.length < maxPaths) {
    const current = queue.shift()
    if (!current) {
      continue
    }

    const lastTypeName = current.typeNames[current.typeNames.length - 1]
    const nextEdges = index.edgesByType.get(normaliseIdentifier(lastTypeName)) ?? []

    for (const edge of nextEdges) {
      if (current.typeNames.some((typeName) => sameIdentifier(typeName, edge.toType))) {
        continue
      }

      const nextPath: RelationshipPath = {
        typeNames: [...current.typeNames, edge.toType],
        datasetNames: [...current.datasetNames, edge.datasetName],
      }
      const pathKey = normaliseIdentifier(`${nextPath.typeNames.join("->")}::${nextPath.datasetNames.join("->")}`)

      if (!seen.has(pathKey)) {
        seen.add(pathKey)
        paths.push(nextPath)
      }

      if (nextPath.datasetNames.length < maxDepth && queue.length < maxPaths * 3) {
        queue.push(nextPath)
      }

      if (paths.length >= maxPaths) {
        break
      }
    }
  }

  return paths
}

export function findContentRelatedViews(
  index: ContentModelIndex,
  options: {
    typeNames: string[]
    datasetNames: string[]
    currentViewName?: string
    maxViews?: number
  }
): ContentViewReference[] {
  // Score views by how many dataset/type hints they match so strongest candidates
  // appear first in inspectors.
  const maxViews = options.maxViews ?? 10
  const relevantTypes = uniqueStrings(options.typeNames)
  const relevantDatasets = uniqueStrings(options.datasetNames)

  return index.views
    .map((view) => {
      const matchedBy = uniqueStrings([
        ...relevantDatasets
          .filter((datasetName) => view.datasetNames.some((candidate) => sameIdentifier(candidate, datasetName)))
          .map((datasetName) => `Dataset: ${datasetName}`),
        ...relevantTypes
          .filter((typeName) =>
            [...view.rowTypeNames, ...view.columnTypeNames].some((candidate) => sameIdentifier(candidate, typeName))
          )
          .map((typeName) => `Type: ${typeName}`),
      ])

      return {
        name: view.name,
        tableType: view.tableType,
        rowTypeNames: [...view.rowTypeNames],
        columnTypeNames: [...view.columnTypeNames],
        matchedBy,
        score:
          matchedBy.length * 10 +
          (options.currentViewName && sameIdentifier(options.currentViewName, view.name) ? 3 : 0),
      }
    })
    .filter((view) => view.matchedBy.length > 0)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score
      }
      return left.name.localeCompare(right.name)
    })
    .slice(0, maxViews)
    .map(({ score: _score, ...view }) => view)
}

export function findContentRelatedPages(
  index: ContentModelIndex,
  options: {
    typeNames: string[]
    relatedViews: ContentViewReference[]
    maxPages?: number
  }
): ContentPageReference[] {
  // Merge widget usage and page context hints, then rank by relevance.
  const maxPages = options.maxPages ?? 10
  const relevantTypes = uniqueStrings(options.typeNames)
  const pageReferences = new Map<string, ContentPageReference & { score: number }>()

  for (const relatedView of options.relatedViews) {
    const widgets = index.widgetsByViewName.get(normaliseIdentifier(relatedView.name)) ?? []

    for (const widget of widgets) {
      const pageInfo = index.pageInfoByName.get(normaliseIdentifier(widget.pageName))
      const matchedBy = uniqueStrings([
        `View: ${relatedView.name}`,
        ...relatedView.matchedBy,
        ...relevantTypes
          .filter((typeName) => widget.contextTypes.some((candidate) => sameIdentifier(candidate, typeName)))
          .map((typeName) => `Context: ${typeName}`),
      ])
      const key = [
        normaliseIdentifier(widget.pageName),
        normaliseIdentifier(widget.sectionTitle ?? ""),
        normaliseIdentifier(widget.sectionId ?? ""),
        normaliseIdentifier(widget.widgetTitle ?? ""),
        normaliseIdentifier(widget.widgetId ?? ""),
        normaliseIdentifier(widget.viewName),
      ].join("|")

      const nextReference: ContentPageReference & { score: number } = {
        name: pageInfo?.name ?? widget.pageName,
        parentName: pageInfo?.parentName,
        sectionTitle: widget.sectionTitle,
        sectionId: widget.sectionId,
        widgetTitle: widget.widgetTitle,
        widgetId: widget.widgetId,
        viewName: widget.viewName,
        matchedBy,
        score: matchedBy.length * 10 + 10,
      }
      const existing = pageReferences.get(key)

      if (!existing || nextReference.score > existing.score) {
        pageReferences.set(key, nextReference)
      } else if (existing) {
        existing.matchedBy = uniqueStrings([...existing.matchedBy, ...nextReference.matchedBy])
      }
    }
  }

  for (const pageInfo of index.pageInfoByName.values()) {
    const contextMatches = relevantTypes.filter((typeName) =>
      pageInfo.contextTypes.some((candidate) => sameIdentifier(candidate, typeName))
    )
    if (contextMatches.length === 0) {
      continue
    }

    const key = [
      normaliseIdentifier(pageInfo.name),
      normaliseIdentifier(pageInfo.parentName ?? ""),
      "context",
    ].join("|")
    const matchedBy = contextMatches.map((typeName) => `Context: ${typeName}`)
    const existing = pageReferences.get(key)

    if (existing) {
      existing.matchedBy = uniqueStrings([...existing.matchedBy, ...matchedBy])
      continue
    }

    pageReferences.set(key, {
      name: pageInfo.name,
      parentName: pageInfo.parentName,
      matchedBy,
      score: matchedBy.length * 5,
    })
  }

  return [...pageReferences.values()]
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score
      }
      return left.name.localeCompare(right.name)
    })
    .slice(0, maxPages)
    .map(({ score: _score, ...pageReference }) => pageReference)
}

export function buildContentReferenceSummary(
  index: ContentModelIndex | undefined,
  options: {
    seedTypes: string[]
    relationshipDatasets: RelationshipDatasetLike[]
    datasetNames: string[]
    currentViewName?: string
    maxDepth?: number
    maxPaths?: number
    maxViews?: number
    maxPages?: number
  }
) {
  // Main entry used by panel code: returns all YAML-derived relationship signals
  // in one shape (types, paths, views, pages).
  const relationshipTypes = uniqueStrings(
    options.relationshipDatasets.flatMap((dataset) => [
      ...(dataset.keyTypes ?? []),
      ...(dataset.valueTypes ?? []),
    ])
  )
  const relatedTypeHints = uniqueStrings([
    ...options.seedTypes,
    ...relationshipTypes,
  ])

  if (!index) {
    return {
      relationshipTypes,
      relationshipPaths: [] as RelationshipPath[],
      relatedViews: [] as ContentViewReference[],
      relatedPages: [] as ContentPageReference[],
    }
  }

  const relatedViews = findContentRelatedViews(index, {
    typeNames: relatedTypeHints,
    datasetNames: options.datasetNames,
    currentViewName: options.currentViewName,
    maxViews: options.maxViews,
  })
  const relatedPages = findContentRelatedPages(index, {
    typeNames: relatedTypeHints,
    relatedViews,
    maxPages: options.maxPages,
  })
  const relationshipPaths = findContentRelationshipPaths(
    index,
    options.seedTypes.length > 0 ? options.seedTypes : relatedTypeHints,
    {
      maxDepth: options.maxDepth,
      maxPaths: options.maxPaths,
    }
  )

  return {
    relationshipTypes,
    relationshipPaths,
    relatedViews,
    relatedPages,
  }
}
